import React, { useState, useEffect } from 'react';
import { Dashboard as DashboardType, Transaction } from '../types';
import { dashboardAPI, transactionsAPI } from '../services/api';
import QuarterlyDashboard from './QuarterlyDashboard';
import PersonsReviewQueue from './PersonsReviewQueue';
import TransactionChart from './TransactionChart';
import TransactionsTable from './TransactionsTable';
import CustomerDebtTable from './CustomerDebtTable';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';
import { formatCurrency } from '../utils/currency';

const DashboardFinance: React.FC = () => {
  const [dashboard, setDashboard] = useState<DashboardType | null>(null);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const minYear = 2014;
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSecondaryLoading, setIsSecondaryLoading] = useState<boolean>(false);
  const [showDashboards, setShowDashboards] = useState<boolean>(false);
  const [tbuTransactions, setTbuTransactions] = useState<Transaction[]>([]);
  const [latestUpdatedTransactions, setLatestUpdatedTransactions] = useState<Transaction[]>([]);

  const incompletedTransactions = dashboard?.transactionsNotCompleted ?? [];
  const topCustomerDebts = dashboard?.topCustomersWithDebt ?? [];

  const {
    currentPage: incompletedCurrentPage,
    pageSize: incompletedPageSize,
    paginatedData: paginatedIncompletedTransactions,
    totalItems: incompletedTotalItems,
    goToPage: goToIncompletedPage,
    setPageSize: setIncompletedPageSize
  } = usePagination({ data: incompletedTransactions, initialPageSize: 10 });

  const {
    currentPage: debtsCurrentPage,
    pageSize: debtsPageSize,
    paginatedData: paginatedCustomerDebts,
    totalItems: debtsTotalItems,
    goToPage: goToDebtsPage,
    setPageSize: setDebtsPageSize
  } = usePagination({ data: topCustomerDebts, initialPageSize: 10 });

  const {
    currentPage: tbuCurrentPage,
    pageSize: tbuPageSize,
    paginatedData: paginatedTbuTransactions,
    totalItems: tbuTotalItems,
    goToPage: goToTbuPage,
    setPageSize: setTbuPageSize
  } = usePagination({ data: tbuTransactions, initialPageSize: 10 });

  const {
    currentPage: latestCurrentPage,
    pageSize: latestPageSize,
    paginatedData: paginatedLatestUpdatedTransactions,
    totalItems: latestTotalItems,
    goToPage: goToLatestPage,
    setPageSize: setLatestPageSize
  } = usePagination({ data: latestUpdatedTransactions, initialPageSize: 10 });

  useEffect(() => {
    loadDashboard(currentYear);
  }, [currentYear]);

  const loadDashboard = async (year: number) => {
    setIsLoading(true);
    setIsSecondaryLoading(true);
    setError(null);
    setTbuTransactions([]);
    setLatestUpdatedTransactions([]);

    try {
      const dashboardData = await dashboardAPI.getDashboard(year);
      setDashboard(dashboardData);
      setIsLoading(false);

      const [tbuData, latestUpdatedData] = await Promise.all([
        transactionsAPI.getTbuTransactions().catch(() => []),
        transactionsAPI.getLatestUpdatedTransactions(year, 25).catch(() => [])
      ]);

      setTbuTransactions(tbuData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      setLatestUpdatedTransactions(latestUpdatedData);
    } catch (loadError) {
      console.error('Error loading dashboard:', loadError);
      setError('Failed to load dashboard');
      setDashboard(null);
      setIsLoading(false);
    } finally {
      setIsSecondaryLoading(false);
    }
  };

  const generateYearButtons = () => {
    const years = [];
    for (let year = new Date().getFullYear(); year >= minYear; year--) {
      years.push(
        <button
          key={year}
          className={`btn ${currentYear === year ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setCurrentYear(year)}
        >
          {year}
        </button>
      );
    }
    return years;
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem'
        }}
      >
        <div className="spinner-border" role="status"></div>
        <p style={{ color: 'var(--text-muted)' }}>Loading dashboard...</p>
      </div>
    );
  }

  if (!dashboard) {
    return <div>Error loading dashboard</div>;
  }

  const revenueValue = currentYear === 0 ? dashboard.totalRevenue : dashboard.revenue;
  const revenueIncludingNotPaidValue =
    currentYear === 0 ? dashboard.totalRevenueIncludingNotPaid : dashboard.revenueIncludingNotPaid;
  const paidToMikeValue = currentYear === 0 ? dashboard.totalPaidToMikeAmount : dashboard.paidToMikeAmount;
  const paidToAlexValue = currentYear === 0 ? dashboard.totalPaidToAlexAmount : dashboard.paidToAlexAmount;

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '2rem' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)' }}>
          {currentYear === 0 ? 'All time overview' : `${currentYear} Overview`}
        </p>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-body">
          <div className="d-flex gap-2 flex-wrap">
            {generateYearButtons()}
            <button
              className={`btn ${currentYear === 0 ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCurrentYear(0)}
            >
              All data
            </button>
          </div>
        </div>
      </div>

      <div className="row g-4" style={{ marginBottom: '2rem' }}>
        <div className="col-md-6 col-xl-3">
          <div className="stats-card">
            <div className="stats-value">{formatCurrency(dashboard.yearTotalInUsd, 'USD')}</div>
            <div className="stats-label">Year profit total</div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div className="stats-card">
            <div className="stats-value">{formatCurrency(dashboard.yearTotalInUsdIncludingNotPaid, 'USD')}</div>
            <div className="stats-label">Year profit total (including not paid)</div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div className="stats-card">
            <div className="stats-value">{formatCurrency(dashboard.totalUnpaidAmount, 'USD')}</div>
            <div className="stats-label">Unpaid</div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div className="stats-card">
            <div className="stats-value">{formatCurrency(revenueValue, 'USD')}</div>
            <div className="stats-label">Revenue</div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div className="stats-card">
            <div className="stats-value">{formatCurrency(revenueIncludingNotPaidValue, 'USD')}</div>
            <div className="stats-label">Revenue (including not paid)</div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div className="stats-card">
            <div className="stats-value">{formatCurrency(paidToMikeValue, 'USD')}</div>
            <div className="stats-label">Paid to Mike</div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div className="stats-card">
            <div className="stats-value">{formatCurrency(paidToAlexValue, 'USD')}</div>
            <div className="stats-label">Paid to Alex</div>
          </div>
        </div>
      </div>

      <div className="chart-container">
        <TransactionChart chartData={dashboard.chart} />
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <button
          className={`btn ${showDashboards ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setShowDashboards(!showDashboards)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {showDashboards ? 'Hide' : 'Show'} Quarterly Dashboards
        </button>
      </div>

      {showDashboards && (
        <div style={{ marginBottom: '2rem' }}>
          <QuarterlyDashboard quarters={dashboard.dashboards} />
        </div>
      )}

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <h5 className="card-title">Incomplete</h5>
        </div>
        <div className="card-body">
          <div className="alert alert-warning mb-3">
            <strong>Total unpaid:</strong> {formatCurrency(dashboard.totalUnpaidAmount, 'USD')}
          </div>
          {incompletedTransactions.length === 0 ? (
            <p className="text-muted mb-0">No incomplete transactions found.</p>
          ) : (
            <>
              <TransactionsTable
                transactions={paginatedIncompletedTransactions}
                showDirectionBadge={true}
                directionBadgeColor="#4b5563"
                sourceBadgeColor="#6b7280"
                highlightPendingStatus={true}
                pendingStatusColor="#111111"
                statusBadgeClassName="badge contact-type-badge"
                showActions={true}
                onEdit={(transaction) => {
                  window.location.href = `/transactions?editId=${transaction.id}`;
                }}
              />
              <Pagination
                currentPage={incompletedCurrentPage}
                totalItems={incompletedTotalItems}
                itemsPerPage={incompletedPageSize}
                onPageChange={goToIncompletedPage}
                onPageSizeChange={setIncompletedPageSize}
                pageSizeOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <h5 className="card-title">Top Customer Debts</h5>
        </div>
        <div className="card-body">
          {topCustomerDebts.length === 0 ? (
            <p className="text-muted mb-0">No customer debts found.</p>
          ) : (
            <>
              <CustomerDebtTable customerDebts={paginatedCustomerDebts} />
              <Pagination
                currentPage={debtsCurrentPage}
                totalItems={debtsTotalItems}
                itemsPerPage={debtsPageSize}
                onPageChange={goToDebtsPage}
                onPageSizeChange={setDebtsPageSize}
                pageSizeOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <h5 className="card-title">To be updated</h5>
        </div>
        <div className="card-body">
          {isSecondaryLoading ? (
            <p className="text-muted mb-0">Loading transactions...</p>
          ) : tbuTransactions.length === 0 ? (
            <p className="text-muted mb-0">No transactions marked as TBU.</p>
          ) : (
            <>
              <TransactionsTable
                transactions={paginatedTbuTransactions}
                showDirectionBadge={true}
                directionBadgeColor="#4b5563"
                sourceBadgeColor="#6b7280"
              />
              <Pagination
                currentPage={tbuCurrentPage}
                totalItems={tbuTotalItems}
                itemsPerPage={tbuPageSize}
                onPageChange={goToTbuPage}
                onPageSizeChange={setTbuPageSize}
                pageSizeOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <h5 className="card-title">Latest updated</h5>
        </div>
        <div className="card-body">
          {isSecondaryLoading ? (
            <p className="text-muted mb-0">Loading transactions...</p>
          ) : latestUpdatedTransactions.length === 0 ? (
            <p className="text-muted mb-0">No updated transactions found.</p>
          ) : (
            <>
              <TransactionsTable
                transactions={paginatedLatestUpdatedTransactions}
                showDirectionBadge={true}
                directionBadgeColor="#4b5563"
                sourceBadgeColor="#6b7280"
              />
              <Pagination
                currentPage={latestCurrentPage}
                totalItems={latestTotalItems}
                itemsPerPage={latestPageSize}
                onPageChange={goToLatestPage}
                onPageSizeChange={setLatestPageSize}
                pageSizeOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => (
  <div className="fade-in">
    <h1 style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-3xl)', fontWeight: '700', marginBottom: '1rem' }}>Dashboard</h1>
    <DashboardFinance />
    <PersonsReviewQueue />
  </div>
);

export default Dashboard;
